import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  Brain,
  Phone,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  Monitor,
  Laptop,
  Wifi,
  MessageCircle,
  FileText,
  PlayCircle,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I'm interested in Online Class 10 Tuition (all subjects). Please share batch details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "5", label: "Subjects Covered", icon: BookOpen },
  { value: "Live", label: "Interactive Classes", icon: Monitor },
  { value: "95%", label: "Success Rate", icon: TrendingUp },
  { value: "15+", label: "Expert Educators", icon: GraduationCap },
];

const ONLINE_SUBJECTS = [
  { subject: "Mathematics", icon: "📐", desc: "Live concept-building, step-by-step problem solving and weekly practice sets covering the complete Class 10 syllabus." },
  { subject: "Science", icon: "🔬", desc: "Physics, Chemistry and Biology explained with real-life examples, diagrams and NCERT-based practice questions." },
  { subject: "Social Science", icon: "🗺️", desc: "History, Geography, Civics and Economics simplified with maps, timelines and structured notes for quick revision." },
  { subject: "English", icon: "📖", desc: "Grammar, writing skills and literature covered with board-pattern practice and personalised feedback." },
  { subject: "Hindi", icon: "🖊️", desc: "Gadya, Padya, vyakaran and lekhan — taught in CBSE board exam format with regular practice." },
];

const ONLINE_FEATURES = [
  { icon: Monitor, title: "Live Interactive Classes", desc: "Real teachers, real-time doubt solving — not just pre-recorded videos. Every class is two-way and interactive." },
  { icon: PlayCircle, title: "Recorded Sessions", desc: "Missed a class? Every session is recorded so students can revise topics anytime, anywhere." },
  { icon: FileText, title: "Digital Notes & Worksheets", desc: "Chapter-wise notes, practice worksheets and previous year questions shared digitally after every class." },
  { icon: Target, title: "Weekly Online Tests", desc: "Regular online assessments with instant results help track progress across all five subjects." },
  { icon: MessageCircle, title: "Doubt Support", desc: "Dedicated doubt-clearing sessions and WhatsApp support ensure no question goes unanswered." },
  { icon: Wifi, title: "Flexible & Convenient", desc: "Attend from home — perfect for students who travel, live far from the centre, or prefer a flexible schedule." },
  { icon: Users, title: "Small Online Batches", desc: "Online doesn't mean crowded. Small batch sizes ensure every student gets individual attention, even online." },
  { icon: Award, title: "Same Faculty, Same Quality", desc: "Online classes are taught by the same expert SSS Achievers faculty trusted by 340+ students in Ghazipur." },
];

const COMPARISON = [
  { feature: "Class Format", sss: "Live, interactive classes", others: "Pre-recorded videos only" },
  { feature: "Doubt Clearing", sss: "Real-time + dedicated sessions", others: "Rarely available" },
  { feature: "Batch Size", sss: "Small Batch (focused)", others: "Hundreds per video" },
  { feature: "Subject Coverage", sss: "All 5 subjects, one place", others: "Scattered across apps" },
  { feature: "Progress Tracking", sss: "Weekly tests + feedback", others: "Self-paced, no feedback" },
  { feature: "Faculty", sss: "Local experts, board-focused", others: "Generic, non-local content" },
  { feature: "Notes & Material", sss: "Included digitally", others: "Often paid separately" },
];

const TESTIMONIALS = [
  { name: "Ankit Rai", cls: "Class 10 Online Batch, 2025", rating: 5, text: "I couldn't travel to the centre due to health issues, so I joined the online Class 10 batch. The live classes felt exactly like being in the classroom. Scored 91% in boards!" },
  { name: "Neha Singh (Parent)", cls: "Parent of Class 10 Student", rating: 5, text: "The online classes from SSS Achievers gave my daughter the same quality teaching without the daily commute. Weekly tests and digital notes kept her on track for all subjects." },
  { name: "Aryan Kushwaha", cls: "Class 10 Online Batch, 2025", rating: 5, text: "All five subjects in one online batch — Maths, Science, SST, English and Hindi. The doubt-clearing sessions on WhatsApp were super helpful before exams." },
  { name: "Ritika Verma", cls: "Class 10, 2025", rating: 5, text: "I live outside Ghazipur city and online tuition from SSS Achievers made it possible to get the same board-focused coaching. Highly recommend for anyone who can't attend in person." },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function OnlineClass10TuitionGhazipur() {
  return (
    <>
      <Helmet>
        <title>Online Class 10 Tuition – All Subjects | SSS Achievers Ghazipur</title>
        <meta
          name="description"
          content="Join online Class 10 tuition for all subjects — Maths, Science, Social Science, English & Hindi — with live interactive classes, weekly tests and expert faculty at SSS Achievers Ghazipur. Free demo class available."
        />
        <meta
          name="keywords"
          content="online class 10th all subject, online class 10 tuition, online tuition Ghazipur, online coaching Ghazipur, class 10 online classes, live online classes for class 10, online CBSE class 10 coaching, SSS Achievers online classes"
        />
        <link rel="canonical" href="https://www.sssachievers.com/online-class-10-tuition-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Online Class 10 Tuition – All Subjects | SSS Achievers" />
        <meta property="og:description" content="Live online Class 10 tuition for all subjects with expert faculty, weekly tests and digital notes. Free demo class at SSS Achievers Ghazipur." />
        <meta property="og:url" content="https://www.sssachievers.com/online-class-10-tuition-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Online Class 10 Tuition – All Subjects | SSS Achievers" />
        <meta name="twitter:description" content="Live online Class 10 classes for all subjects — Maths, Science, SST, English, Hindi. Free demo class at SSS Achievers Ghazipur." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Online Class 10 Tuition Ghazipur", "Online Tuition All Subjects"],
                "description": "SSS Achievers offers online Class 10 tuition covering all subjects — Mathematics, Science, Social Science, English and Hindi — with live interactive classes and expert faculty.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                }
              },
              {
                "@type": "Course",
                "name": "Online Class 10 Tuition - All Subjects",
                "description": "Live online tuition for Class 10 covering Mathematics, Science, Social Science, English and Hindi with weekly tests and digital notes.",
                "provider": {
                  "@type": "EducationalOrganization",
                  "name": "SSS Achievers",
                  "sameAs": "https://www.sssachievers.com"
                },
                "courseMode": "online"
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers offer online classes for Class 10 all subjects?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers complete online Class 10 tuition covering all five subjects: Mathematics, Science, Social Science, English and Hindi, taught live by expert faculty."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Are the online classes live or pre-recorded?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Our online Class 10 classes are live and interactive, allowing real-time doubt clearing. Every session is also recorded so students can revise anytime."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How is SSS Achievers' online tuition different from free YouTube videos or apps?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Unlike generic videos, our online classes are live, interactive, taught by the same expert faculty trusted by 340+ students, include weekly tests, digital notes and dedicated doubt-clearing support."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can students from outside Ghazipur join the online Class 10 batch?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — the online Class 10 batch is open to students anywhere with an internet connection, while maintaining the same board-focused curriculum used at our Ghazipur centre."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is there a free demo for the online Class 10 batch?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a free demo class for the online Class 10 batch so students and parents can experience the teaching quality before enrolling."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the fee for online Class 10 tuition at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Fees for the online Class 10 batch are available on request. Contact us at +91 99185 19991 or WhatsApp for current fee details and available installment options."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Online Class 10 Tuition - All Subjects", "item": "https://www.sssachievers.com/online-class-10-tuition-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/courses" className="hover:text-[#0097b2] transition">Courses</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">Online Class 10 Tuition</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Laptop className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">Live Online Classes — All Subjects</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Online <span className="text-[#0097b2]">Class 10 Tuition</span> — All Subjects, One Platform
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  Learn Mathematics, Science, Social Science, English and Hindi through live, interactive online classes — taught by the same expert faculty trusted by 340+ students at SSS Achievers, Ghazipur. Study from anywhere, without compromising on quality.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition">
                    Book Free Online Demo <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition">
                    <Phone className="w-4 h-4" /> Call: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["All 5 Subjects Covered", "Live Interactive Classes", "Recorded for Revision", "Free Demo Class"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="grid grid-cols-2 gap-4">
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">Trusted by 340+ students</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Mode</p>
                    <p className="text-2xl font-bold">Online</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ SUBJECTS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Every Subject Covered in Our <span className="text-[#0097b2]">Online Class 10 Batch</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">One online batch — all five CBSE subjects, taught by specialist faculty</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {ONLINE_SUBJECTS.map(({ subject, icon, desc }, i) => (
                <motion.div key={subject} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 bg-white p-6 hover:shadow-lg transition-all">
                  <div className="text-3xl mb-3">{icon}</div>
                  <h3 className="font-bold text-slate-800 text-lg mb-2">{subject}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ FEATURES ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Why Choose <span className="text-[#0097b2]">Online Tuition</span> at SSS Achievers
              </motion.h2>
              <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base">All the benefits of our classroom coaching — delivered live, online, to your home.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {ONLINE_FEATURES.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ COMPARISON ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                SSS Achievers Online vs. Generic Online Learning Apps
              </motion.h2>
            </div>
            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">SSS Achievers ✓</div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">Other Apps</div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div key={feature} className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{feature}</div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">{others}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Students Learning <span className="text-[#0097b2]">Online with SSS Achievers</span>
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, cls, rating, text }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ ONLINE OR OFFLINE ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-5">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Prefer <span className="text-[#0097b2]">Classroom Coaching</span> Instead?
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  SSS Achievers also offers in-person Class 10 board preparation at our Ghazipur centre — with the same expert faculty, full mock tests and structured revision plan. Choose whichever mode suits you best, or switch anytime.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/class-10-coaching-ghazipur" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] transition shadow-md text-sm">
                    View Classroom Class 10 Coaching <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/courses/class-10-board-preparation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition text-sm">
                    Class 10 Course Details
                  </Link>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg p-8 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
                <div className="space-y-4">
                  {[
                    { icon: Monitor, text: "Live online classes from home" },
                    { icon: Users, text: "In-person classroom batches in Ghazipur" },
                    { icon: Brain, text: "Hybrid — mix online and offline as needed" },
                    { icon: Shield, text: "Same syllabus, same faculty, same results" },
                  ].map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                      <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#0097b2]" />
                      </div>
                      <p className="text-sm font-semibold text-slate-700">{text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About online Class 10 tuition at SSS Achievers</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Does SSS Achievers offer online classes for Class 10 all subjects?", a: "Yes — SSS Achievers offers complete online Class 10 tuition covering all five subjects: Mathematics, Science, Social Science, English and Hindi, taught live by expert faculty." },
                { q: "Are the online classes live or pre-recorded?", a: "Our online Class 10 classes are live and interactive, allowing real-time doubt clearing. Every session is also recorded so students can revise anytime." },
                { q: "How is this different from free YouTube videos or learning apps?", a: "Unlike generic videos, our online classes are live, interactive, taught by the same expert faculty trusted by 340+ students, and include weekly tests, digital notes and dedicated doubt-clearing support." },
                { q: "Can students from outside Ghazipur join the online batch?", a: "Yes — the online Class 10 batch is open to students anywhere with an internet connection, while following the same board-focused curriculum used at our Ghazipur centre." },
                { q: "Is there a free demo for the online Class 10 batch?", a: "Yes — SSS Achievers offers a free demo class for the online Class 10 batch so students and parents can experience the teaching quality before enrolling." },
                { q: "What is the fee for online Class 10 tuition?", a: "Fees for the online Class 10 batch are available on request. Contact us at +91 99185 19991 or WhatsApp for current details and installment options." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Laptop className="w-4 h-4 text-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Online Class 10 Tuition — All Subjects</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Start Your Online Class 10 Journey Today</h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Book a free online demo class and experience live, interactive Class 10 coaching for all subjects — from the comfort of your home.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Online Demo <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
